import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LoginService } from './login.service';

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {
  // Production: 1 hour session timeout (TEST_MODE = false)
  private readonly TEST_MODE = false;

  // Production values (set TEST_MODE = false before release)
  private readonly CHECK_INTERVAL_PROD = 1000 * 60 * 10; // Check every 10 minutes
  private readonly SESSION_TIMEOUT_MINUTES_PROD = 60; // 1 hour session timeout
  private readonly WARNING_THRESHOLD_MINUTES_PROD = 5; // Show warning 5 minutes before timeout

  // Test values — 5 minute session timeout
  private readonly CHECK_INTERVAL_TEST = 1000 * 10; // Check every 10 seconds
  private readonly SESSION_TIMEOUT_MINUTES_TEST = 5; // 5 minutes for testing
  private readonly WARNING_THRESHOLD_MINUTES_TEST = 1; // Show warning 1 minute before timeout

  private readonly ACTIVITY_DELAY_PROD = 1000 * 60 * 2; // wait 2 minutes of inactivity before polling
  private readonly ACTIVITY_DELAY_TEST = 1000 * 30; // 30 seconds for testing
  private readonly API_CALL_THRESHOLD_TEST = 1; // Call API after 1 minute of inactivity in test mode
  private readonly API_CALL_THRESHOLD_PROD = 5; // Call API after 5 minutes of inactivity in production

  // Active values (switched based on TEST_MODE)
  private CHECK_INTERVAL: number;
  private SESSION_TIMEOUT_MINUTES: number;
  private WARNING_THRESHOLD_MINUTES: number;
  private ACTIVITY_DELAY: number;
  private API_CALL_THRESHOLD: number;

  private inactivityTimeout: any;
  private pollingInterval: any;
  private lastActivityTime: Date = new Date();
  private warningSubject = new Subject<{ minutes: number; seconds: number }>();
  private sessionExpiredSubject = new Subject<void>();
  private activityResumedSubject = new Subject<void>();

  public warning$ = this.warningSubject.asObservable();
  public sessionExpired$ = this.sessionExpiredSubject.asObservable();
  public activityResumed$ = this.activityResumedSubject.asObservable();

  private activityEvents: string[] = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  private activitySubscription: Subscription;
  private warningShown: boolean = false; // Track if warning was already shown
  private sessionStartTime: Date = new Date(); // Track when session monitoring started

  constructor(
    private http: HttpClient,
    private router: Router,
    private loginService: LoginService
  ) {
    // Set active values based on test mode
    if (this.TEST_MODE) {
      this.CHECK_INTERVAL = this.CHECK_INTERVAL_TEST;
      this.SESSION_TIMEOUT_MINUTES = this.SESSION_TIMEOUT_MINUTES_TEST;
      this.WARNING_THRESHOLD_MINUTES = this.WARNING_THRESHOLD_MINUTES_TEST;
      this.ACTIVITY_DELAY = this.ACTIVITY_DELAY_TEST;
      this.API_CALL_THRESHOLD = this.API_CALL_THRESHOLD_TEST;
    } else {
      this.CHECK_INTERVAL = this.CHECK_INTERVAL_PROD;
      this.SESSION_TIMEOUT_MINUTES = this.SESSION_TIMEOUT_MINUTES_PROD;
      this.WARNING_THRESHOLD_MINUTES = this.WARNING_THRESHOLD_MINUTES_PROD;
      this.ACTIVITY_DELAY = this.ACTIVITY_DELAY_PROD;
      this.API_CALL_THRESHOLD = this.API_CALL_THRESHOLD_PROD;
    }

    this.setupActivityTracking();
    this.setupNavigationTracking();

    if (this.TEST_MODE) {
      this.log('Test mode enabled', {
        timeoutMinutes: this.SESSION_TIMEOUT_MINUTES,
        warningBeforeMinutes: this.WARNING_THRESHOLD_MINUTES,
        checkEverySeconds: this.CHECK_INTERVAL / 1000,
        pollingStartsAfterSeconds: this.ACTIVITY_DELAY / 1000,
      });
    }
  }

  private log(message: string, data?: object): void {
    if (!this.TEST_MODE) {
      return;
    }

    if (data) {
      console.log(`[Session Timeout] ${message}`, data);
    } else {
      console.log(`[Session Timeout] ${message}`);
    }
  }

  /**
   * Returns true when session timeout should be suppressed (login/public pages).
   */
  isLoginOrPublicPage(): boolean {
    const url = this.router.url || '';
    const hashPath = (window.location.hash || '').replace(/^#/, '');

    const isPublicPath = (path: string): boolean => {
      if (!path || path === '/') {
        return true;
      }

      return (
        path.startsWith('/login') ||
        path.includes('/login-with-otp') ||
        path.includes('/Submit/LoginOTP') ||
        path.includes('/forgot-password') ||
        path.includes('/autologin') ||
        path.includes('/logout') ||
        path.includes('/webex') ||
        path.includes('/otp-login') ||
        path.startsWith('/create-trial-') ||
        path.startsWith('/trial-user-list')
      );
    };

    return isPublicPath(url) || isPublicPath(hashPath);
  }

  /**
   * Start the session timeout monitoring
   */
  startSessionMonitoring(): void {
    if (this.isLoginOrPublicPage() || !localStorage.getItem('token')) {
      this.log('Monitoring skipped (login page or no token)');
      this.stopSessionMonitoring();
      return;
    }

    this.log('Monitoring started');
    // Clear any existing interval
    this.stopSessionMonitoring();

    // Reset warning flag and session start time
    this.warningShown = false;
    this.sessionStartTime = new Date();

    // Update last activity time
    this.updateLastActivityTime();

    // Initial check once on start
    this.checkSessionStatus();
  }

  /**
   * Stop the session timeout monitoring
   */
  stopSessionMonitoring(): void {
    if (this.pollingInterval || this.inactivityTimeout) {
      this.log('Monitoring stopped');
    }
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
      this.inactivityTimeout = null;
    }
    this.warningShown = false;
  }

  /**
   * Update the last activity time
   */
  updateLastActivityTime(): void {
    this.lastActivityTime = new Date();
    if (this.warningShown) {
      this.warningShown = false;
      this.activityResumedSubject.next();
    }
    this.stopPolling();
    this.scheduleInactivityCheck();
  }

  /**
   * Setup activity tracking to detect user interactions
   */
  private setupActivityTracking(): void {
    this.activityEvents.forEach(event => {
      document.addEventListener(event, () => {
        if (localStorage.getItem('token') && !this.isLoginOrPublicPage()) {
          this.updateLastActivityTime();
        }
      }, true);
    });
  }

  private setupNavigationTracking(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isLoginOrPublicPage()) {
          this.stopSessionMonitoring();
          return;
        }

        if (localStorage.getItem('token')) {
          this.updateLastActivityTime();
        }
      });
  }

  /**
   * Schedule inactivity check after user stops interacting
   */
  private scheduleInactivityCheck(): void {
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
    }
    this.inactivityTimeout = setTimeout(() => {
      this.log('Inactivity detected — starting session checks');
      this.beginPolling();
    }, this.ACTIVITY_DELAY);
  }

  /**
   * Begin polling the session status at regular interval
   * If warning is shown, poll more frequently to keep countdown accurate
   */
  private beginPolling(): void {
    this.stopPolling();
    this.checkSessionStatus();
    // Poll more frequently when warning is shown (every 5 seconds) to keep countdown accurate
    const pollInterval = this.warningShown ? 5000 : this.CHECK_INTERVAL;
    this.pollingInterval = setInterval(() => this.checkSessionStatus(), pollInterval);
  }

  /**
   * Stop polling if user becomes active again
   */
  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Check session status with the server
   */
  private checkSessionStatus(): void {
    const token = localStorage.getItem('token');

    if (!token || this.isLoginOrPublicPage()) {
      this.stopSessionMonitoring();
      return;
    }

    // Always use local activity check first (no API call needed when user is active)
    this.checkLocalActivityTime();

    // Only make API call if user has been inactive for a significant time
    // This prevents unnecessary API calls and screen blinking
    const currentTime = new Date().getTime();
    const lastActivity = this.lastActivityTime.getTime();
    const inactiveMinutes = (currentTime - lastActivity) / (1000 * 60);

    // Use dynamic threshold based on test/production mode
    if (inactiveMinutes > this.API_CALL_THRESHOLD) {
      this.http.get(environment.clientUrl + 'checkAactivityTime').subscribe(
        (response: any) => {
          if (response.status === 0) {
            this.handleSessionCheckSuccess(response, token);
          } else if (response.status === 2) {
            if (this.isLoginOrPublicPage()) {
              return;
            }

            // Only logout if warning was already shown
            if (this.warningShown) {
              this.handleSessionExpired();
            } else {
              // Show warning first
              this.showSessionWarning({ minutes: 0, seconds: 30 });
            }
          }
        },
        (error) => {
          // On error, local check already handled it - silently fail to avoid blinking
        }
      );
    }
  }

  /**
   * Handle successful session check response
   */
  private handleSessionCheckSuccess(response: any, token: string): void {
    if (this.isLoginOrPublicPage()) {
      return;
    }

    try {
      const parseJwt = JSON.parse(atob(token.split('.')[1]));
      const tokenExpTimeStamp = parseJwt.exp * 1000;
      const activityTime = new Date(response.data[0].timeStamp);
      const activityTimeStamp = activityTime.getTime();
      const currentTime = new Date().getTime();

      const tokenDiff = tokenExpTimeStamp - currentTime;
      const tokenMinutes = Math.floor((tokenDiff / 1000) / 60);

      const diff = currentTime - activityTimeStamp;
      const inactiveTotalSeconds = Math.floor(diff / 1000);
      const inactiveMinutes = Math.floor(inactiveTotalSeconds / 60);
      const inactiveSeconds = inactiveTotalSeconds % 60;

      // Check if we should show warning FIRST (before checking expiration)
      if (!this.warningShown && inactiveMinutes >= (this.SESSION_TIMEOUT_MINUTES - this.WARNING_THRESHOLD_MINUTES)) {
        const totalRemainingSeconds = (this.SESSION_TIMEOUT_MINUTES * 60) - inactiveTotalSeconds;
        const remainingMinutes = Math.floor(totalRemainingSeconds / 60);
        const remainingSeconds = totalRemainingSeconds % 60;
        this.warningShown = true;
        this.showSessionWarning({
          minutes: Math.max(0, remainingMinutes),
          seconds: Math.max(0, remainingSeconds)
        });
      }
      // Check if session has expired (only after warning was shown)
      else if (inactiveMinutes >= this.SESSION_TIMEOUT_MINUTES) {
        // If warning wasn't shown yet, show it now with 0 time
        if (!this.warningShown) {
          this.warningShown = true;
          this.showSessionWarning({ minutes: 0, seconds: 30 });
          // Give user 30 seconds before logout
          setTimeout(() => {
            this.handleSessionExpired();
          }, 30000);
        } else {
          this.handleSessionExpired();
        }
      }
    } catch (error) {
      console.error('❌ Session Timeout: Error parsing session data:', error);
      // Fallback to local check on error
      if (!this.TEST_MODE) {
        this.checkLocalActivityTime();
      }
    }
  }

  /**
   * Check local activity time as fallback
   */
  private checkLocalActivityTime(): void {
    if (this.isLoginOrPublicPage()) {
      return;
    }

    const currentTime = new Date().getTime();
    const lastActivity = this.lastActivityTime.getTime();
    const diff = currentTime - lastActivity;
    const inactiveTotalSeconds = Math.floor(diff / 1000);
    const inactiveMinutes = inactiveTotalSeconds / 60; // Use decimal for more precision
    const inactiveSeconds = inactiveTotalSeconds % 60;

    // Debug logging in test mode
    if (this.TEST_MODE) {
      this.log('Activity check', {
        inactiveMinutes: inactiveMinutes.toFixed(2),
        timeoutMinutes: this.SESSION_TIMEOUT_MINUTES,
        warningBeforeMinutes: this.WARNING_THRESHOLD_MINUTES,
      });
    }

    // Check if session has expired FIRST - logout immediately when timeout is reached
    if (inactiveMinutes >= this.SESSION_TIMEOUT_MINUTES) {
      this.log('Session expired — logging out', {
        inactiveMinutes: inactiveMinutes.toFixed(2),
        timeoutMinutes: this.SESSION_TIMEOUT_MINUTES,
      });
      // Logout immediately when timeout is reached
      this.handleSessionExpired();
      return;
    }

    // Check if we should show warning (before expiration)
    if (!this.warningShown && inactiveMinutes >= (this.SESSION_TIMEOUT_MINUTES - this.WARNING_THRESHOLD_MINUTES)) {
      const totalRemainingSeconds = (this.SESSION_TIMEOUT_MINUTES * 60) - inactiveTotalSeconds;
      const remainingMinutes = Math.floor(totalRemainingSeconds / 60);
      const remainingSeconds = totalRemainingSeconds % 60;
      this.warningShown = true;

      this.log('Showing warning popup', {
        remainingMinutes,
        remainingSeconds,
      });

      this.showSessionWarning({
        minutes: Math.max(0, remainingMinutes),
        seconds: Math.max(0, remainingSeconds)
      });
    }
  }

  private showSessionWarning(time: { minutes: number; seconds: number }): void {
    if (this.isLoginOrPublicPage()) {
      this.log('Warning suppressed (login/public page)');
      return;
    }

    this.log('Warning emitted', time);
    this.warningSubject.next(time);
  }

  /**
   * TEST METHOD: Manually trigger warning for testing
   * Call this from browser console: window.sessionTimeoutService.triggerTestWarning()
   */
  triggerTestWarning(): void {
    this.log('Manual test warning triggered');
    this.showSessionWarning({ minutes: 0, seconds: 30 });
  }

  /**
   * TEST METHOD: Manually trigger session expired for testing
   * Call this from browser console: window.sessionTimeoutService.triggerTestExpiry()
   */
  triggerTestExpiry(): void {
    this.log('Manual test expiry triggered');
    this.handleSessionExpired();
  }

  /**
   * Public method to trigger session expired (can be called from components)
   */
  public forceSessionExpired(): void {
    this.handleSessionExpired();
  }

  /**
   * Handle session expired
   */
  private handleSessionExpired(): void {
    if (this.isLoginOrPublicPage()) {
      this.stopSessionMonitoring();
      return;
    }

    this.stopSessionMonitoring();
    this.sessionExpiredSubject.next();
    this.logout();
  }

  /**
   * Extend session by updating activity time
   */
  extendSession(): Observable<any> {
    return this.http.get(environment.clientUrl + 'updateAactivityTime');
  }

  /**
   * Logout user and clear session
   */
  private logout(): void {
    this.loginService.logOut().subscribe(
      () => {
        localStorage.clear();
        this.router.navigate(['/login']);
      },
      (error) => {
        console.error('Error during logout:', error);
        localStorage.clear();
        this.router.navigate(['/login']);
      }
    );
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.stopSessionMonitoring();
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
    }
  }
}

